<template>
  <div class="mb-10">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-20">
      <div class="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
      <p class="mt-4 text-gray-600 text-lg">Carregando...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-20 px-4">
      <div class="max-w-md mx-auto">
        <svg class="w-16 h-16 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Erro ao carregar</h2>
        <p class="text-gray-600 mb-6">{{ error }}</p>
        <button 
          @click="retryLoading" 
          class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          Tentar Novamente
        </button>
      </div>
    </div>

    <!-- Conteúdo da Landing Page -->
    <div v-else class="w1200">
      <!-- SEÇÕES FIXAS (sempre no topo) -->
      <EventHeaderSection />
      <AboutEventSection />

      <!-- SEÇÕES DINÂMICAS (ordenadas por sequence) -->
      <template v-for="section in orderedSections" :key="section.id">
        <div :id="section.section_key">
          <RegisterSection v-if="section.section_key === 'register'" />
          <SpeakersSection v-if="section.section_key === 'speakers'" />
          <ScheduleSection v-if="section.section_key === 'schedule'" />
          <TestimonialsSection v-if="section.section_key === 'testimonials'" />
          <SponsorsSection v-if="section.section_key === 'sponsors'" />
          <FaqSection v-if="section.section_key === 'faqs'" />
          <VideosSection v-if="section.section_key === 'videos'" />
        </div>
      </template>
    </div>

    <!-- Debug Info -->
    <div v-if="showDebug" class="fixed bottom-4 right-4 bg-black bg-opacity-75 text-white p-4 rounded-lg text-xs max-w-xs z-50">
      <h4 class="font-bold mb-2">🔍 Debug Info</h4>
      <p>Seções Ativas: {{ orderedSections.length }}</p>
      <p>Total Seções: {{ sections?.length || 0 }}</p>
      <div class="mt-2 max-h-48 overflow-y-auto">
        <p v-for="s in orderedSections" :key="s.id" class="truncate mb-1">
          {{ s.sequence }}. {{ s.section_name }} ({{ s.section_key }})
        </p>
      </div>
      <button 
        @click="showDebug = false" 
        class="mt-2 text-xs bg-red-500 px-2 py-1 rounded hover:bg-red-600"
      >
        Fechar
      </button>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted } from 'vue'
import { useLandingPage } from '@/composables/useLandingPage'

// Importar componentes das seções
import EventHeaderSection from '@/components/sections/EventHeaderSection.vue'
import AboutEventSection from '@/components/sections/AboutEventSection.vue'
import RegisterSection from '@/components/sections/RegisterSection.vue'
import SpeakersSection from '@/components/sections/SpeakersSection.vue'
import ScheduleSection from '@/components/sections/ScheduleSection.vue'
import TestimonialsSection from '@/components/sections/TestimonialsSection.vue'
import SponsorsSection from '@/components/sections/SponsorsSection.vue'
import FaqSection from '@/components/sections/FaqSection.vue'
import VideosSection from '@/components/sections/VideosSection.vue'

export default {
  name: 'f-identificacao',
  
  components: {
    EventHeaderSection,
    AboutEventSection,
    RegisterSection,
    SpeakersSection,
    ScheduleSection,
    TestimonialsSection,
    SponsorsSection,
    FaqSection,
    VideosSection
  },

  setup() {
    const {
      sections,
      settings,
      customization,
      loading,
      error,
      activeSections,
      fetchAll
    } = useLandingPage()

    const showDebug = ref(false)
    
    if (typeof window !== 'undefined') {
      showDebug.value = new URLSearchParams(window.location.search).get('debug') === 'true'
    }

    /**
     * Seções ordenadas por sequence (apenas ativas)
     */
    const orderedSections = computed(() => {
      // null = ainda não carregou da API → fallback
      if (activeSections.value === null) {
        return [
          { id: 0, section_key: 'register', sequence: 1 },
          { id: 1, section_key: 'speakers', sequence: 2 },
          { id: 2, section_key: 'schedule', sequence: 3 },
          { id: 3, section_key: 'testimonials', sequence: 4 },
          { id: 4, section_key: 'sponsors', sequence: 5 },
          { id: 5, section_key: 'faqs', sequence: 6 },
        ]
      }
      // [] = carregou e tudo desativado → respeitar
      // Ordenar por sequence (menor primeiro)
      return [...activeSections.value].sort((a, b) => a.sequence - b.sequence)
    })

    /**
     * Aplicar variáveis CSS globais
     */
    const applyCSSVariables = () => {
      if (!customization.value) return
      
      const root = document.documentElement
      const c = customization.value
      
      if (c.button_color) root.style.setProperty('--button-color', c.button_color)
      if (c.button_hover_color) root.style.setProperty('--button-hover-color', c.button_hover_color)
      if (c.section_title_color) root.style.setProperty('--section-title-color', c.section_title_color)
      if (c.section_subtitle_color) root.style.setProperty('--section-subtitle-color', c.section_subtitle_color)
      if (c.section_divider_color) root.style.setProperty('--section-divider-color', c.section_divider_color)
    }

    /**
     * Atualizar título da página
     */
    const updatePageTitle = () => {
      if (settings.value?.event_name) {
        document.title = settings.value.event_name
      }
      // Atualizar favicon
      if (customization.value?.favicon) {
        let link = document.querySelector("link[rel*='icon']")
        if (!link) {
          link = document.createElement('link')
          link.rel = 'shortcut icon'
          document.head.appendChild(link)
        }
        link.href = customization.value.favicon
      }
    }

    watch(customization, (newVal) => {
      if (newVal) applyCSSVariables()
    }, { immediate: true })

    watch(settings, (newVal) => {
      if (newVal) updatePageTitle()
    }, { immediate: true })

    onMounted(() => {
      if (customization.value) applyCSSVariables()
      if (settings.value) updatePageTitle()
    })

    const retryLoading = () => {
      fetchAll()
    }

    return {
      sections,
      loading,
      error,
      activeSections,
      orderedSections,
      showDebug,
      retryLoading
    }
  }
}
</script>